type IconStyle = "regular" | "solid";

const defaultIcons = {
  save: "fa-floppy-disk",
  edit: "fa-pencil",
  cancel: "fa-circle-xmark",
  delete: "fa-trash-alt",
  drag: "fa-up-down-left-right",
  verticalMenu: "fa-ellipsis-vertical",
  horizontalMenu: "fa-ellipsis-h",
  info: "fa-circle-info",
  draft: "fa-clipboard",
  chat: "fa-comment",
  video: "fa-film",
  add: "fa-circle-plus",
  addCategory: "fa-layer-plus",
  unlink: "fa-link-slash",
  link: "fa-link",
  search: "fa-magnifying-glass",
  view: "fa-eye",
  sloCorePurpose: "fa-bullseye",
  sloTarget: "fa-frame",
  sloExamProgramme: "fa-graduation-cap",
  cluster: "fa-list-tree",
  warning: "fa-triangle-exclamation",
  negative: "fa-circle-xmark",
  required: "fa-hexagon-exclamation",
  positive: "fa-circle-check",
  file: "fa-file",
  actionplan: "fa-file-lines",
  conversationInvitation: "fa-file-lines",
  conversationNote: "fa-file-pen",
  folder: "fa-folder-closed",
  attachment: "fa-paperclip",
  reference: "fa-file-signature",
  image: "fa-image",
  linkAttachment: "fa-link",
  videoAttachment: "fa-video",
  users: "fa-users",
  userGroup: "fa-user-group",
  usersChecked: "fa-user-check",
  calendar: "fa-calendar-days",
  deadline: "fa-alarm-clock",
  source: "fa-map-pin",
  plan: "fa-plus-circle",
  duplicate: "fa-copy",
  import: "fa-download",
  gift: "fa-gift",
  bell: "fa-bell",
  toolbox: "fa-toolbox",
  school: "fa-school",
  messages: "fa-messages",
  settings: "fa-gear",
  externalLink: "fa-arrow-up-right-from-square",
  chevronLeft: "fa-chevron-left",
  logoutIcon: "fa-right-from-bracket",
  message: "fa-message",
  pasteIcon: "fa-paste",
  archive: "fa-box-archive",
  reminder: "fa-bell-on",
  feedbackIcon: "fa-bullhorn",
  sort: "fa-arrow-up-wide-short",
  close: "fa-xmark",
  clearInput: "fa-circle-xmark",
  filter: "fa-sliders",
  print: "fa-print",
  zoom: "fa-magnifying-glass",
  loginWithSchoolAccount: "fa-school",
  loginByEmail: "fa-envelope",
} as const;

const menuIcons = {
  webmaster: "fa-crown",
  learningObjectives: "fa-bullseye-arrow",
  tasks: "fa-list-check",
  actionplans: "fa-clipboard-list-check",
  portfolio: "fa-briefcase",
  overview: "fa-users-viewfinder",
  myMeetings: defaultIcons.messages,
  supportIcon: "fa-circle-question",
  myLessongroups: "fa-people-group",
  mentorClass: "fa-people-line",
  manageIcon: "fa-sliders",
} as const;

const portfolioIcons = {
  task: "fa-file-check",
  tasks: {
    owners: "fa-user-group-crown",
  },
  assessmentModels: {
    grade: "fa-award",
    simple: "fa-award",
  },
  activity: {
    task: "fa-clipboard-check",
    subActivity: "fa-circle-bolt",
    reference: "fa-quote-left",
    reaction: "fa-message-lines",
  },
  edit: "fa-edit",
  star: "fa-star",
  stats: {
    activities: "fa-circle-bolt",
    skills: "fa-puzzle-piece",
    attachments: "fa-file",
    references: "fa-flag",
  },
  publicTask: {
    viewed: "fa-eye",
    planned: "fa-calendar-plus",
  },
} as const;

const editorIcons = {
  bold: "fa-bold",
  italic: "fa-italic",
  underline: "fa-underline",
  ordered: "fa-list-ol",
  unordered: "fa-list-ul",
  image: "fa-image",
  link: "fa-link-simple",
  unlink: "fa-link-simple-slash",
  code: "fa-code",
} as const;

const manageFeatureIcons = {
  publish: "fa-upload",
  unpublish: "fa-eye-slash",
} as const;

const inputIcons = {
  inputDate: "fa-calendar-days",
  inputTime: "fa-clock",
} as const;

export const fileIconsByMimeType: Record<string, string> = {
  default: "fa-paperclip",
  "application/pdf": "fa-file-pdf",
  "application/msword": "fa-file-word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "fa-file-word",
  "application/vnd.ms-powerpoint": "fa-file-ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "fa-file-ppt",
  "application/vnd.ms-excel": "fa-file-xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "fa-file-xls",
  "image/jpeg": "fa-file-jpg",
  "image/png": "fa-file-png",
  "image/gif": "fa-file-gif",
  "image/webp": "fa-file-image",
  "image/heif": "fa-file-image",
  "image/heic": "fa-file-image",
  "text/plain": "fa-file-lines",
  "application/rtf": "fa-file-lines",
  "application/vnd.apple.pages": "fa-file-lines",
  "application/vnd.apple.numbers": "fa-file-lines",
  "application/vnd.apple.keynote": "fa-file-lines",
  "application/vnd.oasis.opendocument.text": "fa-file-lines",
  "application/vnd.oasis.opendocument.presentation": "fa-file-lines",
  "application/vnd.oasis.opendocument.spreadsheet": "fa-file-lines",
  "application/vnd.oasis.opendocument.chart": "fa-file-lines",
  "application/zip": "fa-file-zip",
  "application/x-zip-compressed": "fa-file-zip",
  "application/x-iwork-pages-sffpages": "fa-file-lines",
  "application/x-iwork-keynote-sffkey": "fa-file-lines",
  "application/x-iwork-numbers-sffnumbers": "fa-file-lines",
  "video/mp4": "fa-file-video",
  "video/quicktime": "fa-file-video",
  "video/x-matroska": "fa-file-video",
  "video/webm": "fa-file-video",
  "video/x-msvideo": "fa-file-video",
  "video/x-ms-wmv": "fa-file-video",
};

const chatIcons = {
  send: "fa-paper-plane",
} as const;

export const toDuotoneIcon = (
  icon: string,
  style: IconStyle = "regular",
): string => {
  const styleClass = style === "solid" ? "fa-solid" : "fa-regular";
  return `fa-duotone ${styleClass} ${icon}`;
};

export const toClassicIcon = (
  icon: string,
  style: IconStyle = "regular",
): string => {
  const styleClass = style === "solid" ? "fa-solid" : "fa-regular";
  return `${styleClass} ${icon}`;
};

export const toSolidIcon = (icon: string): string => `fa-solid ${icon}`;

export const saveIcon = defaultIcons.save;
export const editIcon = defaultIcons.edit;
export const cancelIcon = defaultIcons.cancel;
export const deleteIcon = defaultIcons.delete;
export const dragIcon = defaultIcons.drag;
export const verticalMenuIcon = defaultIcons.verticalMenu;
export const horizontalMenuIcon = defaultIcons.horizontalMenu;
export const infoIcon = defaultIcons.info;
export const draftIcon = defaultIcons.draft;
export const chatIcon = defaultIcons.chat;
export const videoIcon = defaultIcons.video;
export const addIcon = defaultIcons.add;
export const searchIcon = defaultIcons.search;
export const viewIcon = defaultIcons.view;
export const unlinkIcon = defaultIcons.unlink;
export const linkIcon = defaultIcons.link;
export const sloCorePurposeIcon = defaultIcons.sloCorePurpose;
export const sloTargetIcon = defaultIcons.sloTarget;
export const sloExamProgrammeIcon = defaultIcons.sloExamProgramme;
export const clusterIcon = defaultIcons.cluster;
export const warningIcon = defaultIcons.warning;
export const negativeIcon = defaultIcons.negative;
export const positiveIcon = defaultIcons.positive;
export const assessmentGradeIcon = portfolioIcons.assessmentModels.grade;
export const assessmentSimpleIcon = portfolioIcons.assessmentModels.simple;
export const portfolioEditIcon = portfolioIcons.edit;
export const requiredIcon = defaultIcons.required;
export const fileIcon = defaultIcons.file;
export const actionplanIcon = defaultIcons.actionplan;
export const conversationInvitationIcon = defaultIcons.conversationInvitation;
export const conversationNoteIcon = defaultIcons.conversationNote;
export const folderIcon = defaultIcons.folder;
export const attachmentIcon = defaultIcons.attachment;
export const referenceIcon = defaultIcons.reference;
export const imageIcon = defaultIcons.image;
export const linkAttachmentIcon = defaultIcons.linkAttachment;
export const videoAttachmentIcon = defaultIcons.videoAttachment;
export const usersIcon = defaultIcons.users;
export const usersCheckedIcon = defaultIcons.usersChecked;
export const calendarIcon = defaultIcons.calendar;
export const deadlineIcon = defaultIcons.deadline;
export const sourceIcon = defaultIcons.source;
export const planIcon = defaultIcons.plan;
export const duplicateIcon = defaultIcons.duplicate;
export const importIcon = defaultIcons.import;
export const messageIcon = defaultIcons.message;
export const giftIcon = defaultIcons.gift;
export const closeIcon = defaultIcons.close;
export const clearInputIcon = defaultIcons.clearInput;
export const filterIcon = defaultIcons.filter;
export const printIcon = defaultIcons.print;
export const zoomIcon = defaultIcons.zoom;

// WYSIWYG Editor icons
export const editorBold = editorIcons.bold;
export const editorItalic = editorIcons.italic;
export const editorUnderline = editorIcons.underline;
export const editorOrdered = editorIcons.ordered;
export const editorUnordered = editorIcons.unordered;
export const editorImage = editorIcons.image;
export const editorLink = editorIcons.link;
export const editorCode = editorIcons.code;
export const editorUnlink = editorIcons.unlink;

// Input icons
export const inputDateIcon = inputIcons.inputDate;
export const inputTimeIcon = inputIcons.inputTime;

// Manage Feature icons
export const manageFeaturePublishIcon = manageFeatureIcons.publish;
export const manageFeatureUnpublishIcon = manageFeatureIcons.unpublish;
export const bellIcon = defaultIcons.bell;
export const toolboxIcon = defaultIcons.toolbox;
export const webmasterIcon = menuIcons.webmaster;
export const learningObjectivesIcon = menuIcons.learningObjectives;
export const tasksIcon = menuIcons.tasks;
export const actionplansIcon = menuIcons.actionplans;

export const portfolioIcon = menuIcons.portfolio;
export const userGroup = defaultIcons.userGroup;

export const overviewIcon = menuIcons.overview;
export const schoolIcon = defaultIcons.school;
export const loginWithSchoolAccountIcon = defaultIcons.loginWithSchoolAccount;
export const loginByEmailIcon = defaultIcons.loginByEmail;
export const messagesIcon = menuIcons.myMeetings;
export const myMeetingsIcon = menuIcons.myMeetings;

export const settingsIcon = defaultIcons.settings;
export const supportIcon = menuIcons.supportIcon;
export const externalLinkIcon = defaultIcons.externalLink;

export const chevronLeftIcon = defaultIcons.chevronLeft;
export const logoutIcon = defaultIcons.logoutIcon;

export const myLessongroupsIcon = menuIcons.myLessongroups;
export const mentorClassIcon = menuIcons.mentorClass;
export const manageIcon = menuIcons.manageIcon;
export const pasteIcon = defaultIcons.pasteIcon;
export const archiveIcon = defaultIcons.archive;
export const reminderIcon = defaultIcons.reminder;

export const taskOwnersIcon = portfolioIcons.tasks.owners;
export const feedbackIcon = defaultIcons.feedbackIcon;
export const sortIcon = defaultIcons.sort;

export const taskIcon = portfolioIcons.task;
export const activityStarIcon = portfolioIcons.star;
export const activityTaskIcon = portfolioIcons.activity.task;
export const activitySubActivityIcon = portfolioIcons.activity.subActivity;
export const activityReferenceIcon = portfolioIcons.activity.reference;

// Public tasks
export const publicTaskViewedIcon = portfolioIcons.publicTask.viewed;
export const publicTaskPlannedIcon = portfolioIcons.publicTask.planned;

// Portfolio student stats icons (duotone)
export const statsActivitiesIcon = portfolioIcons.stats.activities;
export const statsSkillsIcon = portfolioIcons.stats.skills;
export const statsAttachmentsIcon = portfolioIcons.stats.attachments;
export const statsReferencesIcon = portfolioIcons.stats.references;

// Chat icons
export const chatSendIcon = chatIcons.send;
export const addCategoryIcon = defaultIcons.addCategory;
export const portfolioReactionIcon = portfolioIcons.activity.reaction;

// Banner icons (solid style for banners)
export const bannerIcons = {
  info: toSolidIcon(defaultIcons.info),
  warning: toSolidIcon(defaultIcons.warning),
  negative: toSolidIcon(defaultIcons.negative),
  positive: toSolidIcon(defaultIcons.positive),
} as const;
